import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { galleryAPI, vehiclesAPI } from "@/lib/api";
import { HeaderIcon } from "@/lib/assets";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Upload,
  Image as ImageIcon,
  X,
  Trash2,
  ChevronLeft,
  ChevronRight,
  FolderOpen,
  Grid3X3,
  Edit,
} from "lucide-react";

const ALBUM_COLORS = {
  "Build Progress": "bg-blue-500/10 text-blue-500 border-blue-500/30",
  "Trail Shots": "bg-green-500/10 text-green-500 border-green-500/30",
  "Before/After": "bg-purple-500/10 text-purple-500 border-purple-500/30",
  "Detail Shots": "bg-amber-500/10 text-amber-500 border-amber-500/30",
  "Maintenance": "bg-red-500/10 text-red-500 border-red-500/30",
  "Other": "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",
};

export default function PhotoGallery() {
  const { id } = useParams();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [vehicle, setVehicle] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [albumCategories, setAlbumCategories] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState("all");
  const [viewMode, setViewMode] = useState("grid"); // grid or albums
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  
  // Upload state
  const [uploadAlbum, setUploadAlbum] = useState("Other");
  const [uploadCaption, setUploadCaption] = useState("");

  useEffect(() => {
    loadData();
  }, [id]);

  useEffect(() => {
    loadPhotos();
  }, [selectedAlbum]);

  const loadData = async () => {
    try {
      const [vehicleRes, albumsRes, categoriesRes] = await Promise.all([
        vehiclesAPI.get(id),
        galleryAPI.albums(id),
        galleryAPI.albumCategories(),
      ]);
      setVehicle(vehicleRes.data);
      setAlbums(albumsRes.data.albums);
      setAlbumCategories(categoriesRes.data);
      await loadPhotos();
    } catch (error) {
      toast.error("Failed to load gallery");
    } finally {
      setLoading(false);
    }
  };

  const loadPhotos = async () => {
    try {
      const album = selectedAlbum === "all" ? null : selectedAlbum;
      const { data } = await galleryAPI.list(id, album);
      setPhotos(data.photos);
    } catch (error) {
      toast.error("Failed to load photos");
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      await galleryAPI.upload(id, file, uploadAlbum, uploadCaption);
      toast.success("Photo uploaded");
      setUploadCaption("");
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDelete = async (photoId) => {
    if (!confirm("Delete this photo?")) return;
    
    try {
      await galleryAPI.delete(id, photoId);
      toast.success("Photo deleted");
      await loadData();
      if (lightboxOpen) {
        setLightboxOpen(false);
      }
    } catch (error) {
      toast.error("Failed to delete photo");
    }
  };

  const openLightbox = (index) => {
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
  };

  const navigateLightbox = (direction) => {
    if (direction === "prev") {
      setCurrentPhotoIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
    } else {
      setCurrentPhotoIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const currentPhoto = photos[currentPhotoIndex];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link
                to={`/vehicles/${id}`}
                data-testid="back-btn"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-3">
                <HeaderIcon />
                <div>
                  <h1 className="text-lg font-bold text-foreground">Photo Gallery</h1>
                  <p className="text-xs text-muted-foreground">{vehicle?.nickname}</p>
                </div>
              </div>
            </div>

            {/* View Toggle */}
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === "grid" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("grid")}
                className="h-8"
              >
                <Grid3X3 className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === "albums" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("albums")}
                className="h-8"
              >
                <FolderOpen className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Upload Section */}
        <Card className="card-tactical bg-zinc-950/50 border-zinc-800 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm text-muted-foreground mb-1 block">Album</label>
                <Select value={uploadAlbum} onValueChange={setUploadAlbum}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    {albumCategories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm text-muted-foreground mb-1 block">Caption (optional)</label>
                <Input
                  value={uploadCaption}
                  onChange={(e) => setUploadCaption(e.target.value)}
                  placeholder="Add a caption..."
                  className="bg-zinc-900 border-zinc-800"
                />
              </div>
              <div className="flex items-end">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="photo-upload-input"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="upload-photo-btn"
                  className="btn-tactical bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Upload Photo
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {viewMode === "albums" ? (
          /* Albums View */
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {albums.length === 0 ? (
              <Card className="card-tactical bg-zinc-950/50 border-zinc-800 col-span-full">
                <CardContent className="p-8 text-center">
                  <FolderOpen className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-muted-foreground">No photos yet. Upload your first photo!</p>
                </CardContent>
              </Card>
            ) : (
              albums.map((album) => (
                <Card
                  key={album.name}
                  className="card-tactical bg-zinc-950/50 border-zinc-800 hover:border-zinc-700 transition-colors cursor-pointer overflow-hidden"
                  onClick={() => {
                    setSelectedAlbum(album.name);
                    setViewMode("grid");
                  }}
                  data-testid={`album-${album.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="h-40 bg-zinc-900 relative">
                    {album.cover_photo ? (
                      <img
                        src={album.cover_photo}
                        alt={album.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="w-12 h-12 text-zinc-700" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 to-transparent" />
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className={`inline-block px-2 py-1 text-xs rounded-sm border ${ALBUM_COLORS[album.name] || ALBUM_COLORS["Other"]}`}>
                          {album.name}
                        </span>
                        <p className="text-sm text-muted-foreground mt-2">
                          {album.count} {album.count === 1 ? "photo" : "photos"}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        ) : (
          /* Grid View */
          <>
            {/* Album Filter */}
            <div className="flex items-center gap-4 mb-6">
              <Select value={selectedAlbum} onValueChange={setSelectedAlbum}>
                <SelectTrigger className="w-48 bg-zinc-900 border-zinc-800" data-testid="album-filter">
                  <SelectValue placeholder="All Albums" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  <SelectItem value="all">All Albums</SelectItem>
                  {albumCategories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">
                {photos.length} {photos.length === 1 ? "photo" : "photos"}
              </span>
            </div>

            {/* Photo Grid */}
            {photos.length === 0 ? (
              <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
                <CardContent className="p-12 text-center">
                  <ImageIcon className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-foreground mb-2">No Photos</h3>
                  <p className="text-muted-foreground">
                    {selectedAlbum === "all" 
                      ? "Upload your first photo to start building your gallery"
                      : `No photos in "${selectedAlbum}" album`}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
                {photos.map((photo, index) => (
                  <div
                    key={photo.id}
                    className="relative group aspect-square bg-zinc-900 rounded-sm overflow-hidden cursor-pointer"
                    onClick={() => openLightbox(index)}
                    data-testid={`photo-${photo.id}`}
                  >
                    <img
                      src={photo.url}
                      alt={photo.caption || "Vehicle photo"}
                      className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute bottom-0 left-0 right-0 p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className={`inline-block px-2 py-0.5 text-xs rounded-sm border ${ALBUM_COLORS[photo.album] || ALBUM_COLORS["Other"]}`}>
                        {photo.album}
                      </span>
                      {photo.caption && (
                        <p className="text-xs text-white mt-1 line-clamp-1">{photo.caption}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-5xl bg-zinc-950 border-zinc-800 p-0">
          {currentPhoto && (
            <div className="relative">
              {/* Close button */}
              <button
                onClick={() => setLightboxOpen(false)}
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-zinc-900/80 rounded-full flex items-center justify-center text-white hover:bg-zinc-800 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Navigation */}
              {photos.length > 1 && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); navigateLightbox("prev"); }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-zinc-900/80 rounded-full flex items-center justify-center text-white hover:bg-zinc-800 transition-colors"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); navigateLightbox("next"); }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-zinc-900/80 rounded-full flex items-center justify-center text-white hover:bg-zinc-800 transition-colors"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}

              {/* Image */}
              <div className="flex items-center justify-center min-h-[400px] max-h-[70vh]">
                <img
                  src={currentPhoto.url}
                  alt={currentPhoto.caption || "Photo"}
                  className="max-w-full max-h-[70vh] object-contain"
                />
              </div>

              {/* Info Bar */}
              <div className="p-4 border-t border-zinc-800 flex items-center justify-between">
                <div>
                  <span className={`inline-block px-2 py-1 text-xs rounded-sm border ${ALBUM_COLORS[currentPhoto.album] || ALBUM_COLORS["Other"]}`}>
                    {currentPhoto.album}
                  </span>
                  {currentPhoto.caption && (
                    <p className="text-sm text-foreground mt-2">{currentPhoto.caption}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {currentPhotoIndex + 1} of {photos.length}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(currentPhoto.id)}
                  className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
